import { __ } from "@wordpress/i18n";
import Tagify from "@yaireo/tagify";
import "@yaireo/tagify/dist/tagify.css";
import React, {
	useRef,
	useEffect,
	useState,
	forwardRef,
	useImperativeHandle,
} from "react";
import "./TagInput.scss";

const TagInput = forwardRef(
	(
		{ type = "tag", initialTags, whitelist = [], name, handleChange },
		ref,
	) => {
		const tagifyInputRef = useRef(null);
		const tagifyInstanceRef = useRef(null);
		const isInternalChangeRef = useRef(false);
		const lastInitialTagsRef = useRef(null);
		const isInitializedRef = useRef(false);
		const skipNextSyncRef = useRef(false);

		const formattedInitialTags = Array.isArray(initialTags)
			? initialTags.map((item) =>
					item.value && item.code
						? { value: item.value, code: item.code }
						: item,
			  )
			: [];

		const [currentTags, setCurrentTags] = useState(formattedInitialTags);

		// const ipRegex =
		// 	/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
		
		const ipRegex =
  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))|(?:\:\:1)$/;


		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

		useEffect(() => {
            if (isInitializedRef.current || !tagifyInputRef.current) {
                return;
            }
        
            tagifyInstanceRef.current = new Tagify(tagifyInputRef.current, {
                whitelist: whitelist.map(item => (
                    item.value && item.code ? { value: item.value, code: item.code } : item
                )),
                dropdown: {
                    maxItems: whitelist.length,
                    enabled: 0,
                },
                originalInputValueFormat: valuesArr => JSON.stringify(valuesArr),
            });
        
            const instance = tagifyInstanceRef.current;
        
            const handleTagChange = (e) => {
                let parsedTags;
                try {
                    parsedTags = JSON.parse(e.detail.value);
                } catch (error) {
                    parsedTags = [];
                }
        
                setCurrentTags(parsedTags);
                handleChange(name, parsedTags);
                isInternalChangeRef.current = false;
            };
        
            instance.on("change", handleTagChange);
        
            if (type === "ip") {
                instance.on('add', (e) => {
                    const addedTag = e.detail.data;
                    if (!ipRegex.test(addedTag.value)) {
                        instance.removeTag(addedTag.value);
                    }
                });
            } else if (type === "email") {
                instance.on('add', (e) => {
                    const addedTag = e.detail.data;
                    if (!emailRegex.test(addedTag.value)) {
                        instance.removeTag(addedTag.value);
                    }
                });
            }
        
            if (formattedInitialTags.length > 0) {
                isInternalChangeRef.current = true;
                instance.addTags(formattedInitialTags);
            }
        
            lastInitialTagsRef.current = JSON.stringify(formattedInitialTags);
            isInitializedRef.current = true;
        
            return () => {
                if (tagifyInstanceRef.current) {
                    try {
                        tagifyInstanceRef.current.destroy?.();
                    } catch (err) {
                        // console.warn("Tagify destroy() failed", err);
                    }
                    tagifyInstanceRef.current = null;
                }
                isInitializedRef.current = false;
            };
        }, []);
        

		useEffect(() => {
			if (!tagifyInstanceRef.current || !isInitializedRef.current) {
				return;
			}

			const newInitialTagsJSON = JSON.stringify(formattedInitialTags);

			// Skip update if we should skip the next sync (used when adding via imperative handle)
			if (skipNextSyncRef.current) {
				skipNextSyncRef.current = false;
				lastInitialTagsRef.current = newInitialTagsJSON;
				return;
			}

			// Skip update if we're in the middle of an internal change
			if (isInternalChangeRef.current) {
				return;
			}

			// Only update if initialTags actually changed from external source
			// if (newInitialTagsJSON !== lastInitialTagsRef.current) {
			const currentValues = tagifyInstanceRef.current.value || [];
			const currentJSON = JSON.stringify(currentValues);

			// Only update if the current tags don't match the new initial tags
			// if (currentJSON !== newInitialTagsJSON) {
			// Set the internal change flag to prevent the change event from triggering
			isInternalChangeRef.current = true;

			// Use a timeout to ensure the update happens after any pending operations
			setTimeout(() => {
				if (tagifyInstanceRef.current && isInitializedRef.current) {
					tagifyInstanceRef.current.removeAllTags();
					if (formattedInitialTags.length > 0) {
						tagifyInstanceRef.current.addTags(formattedInitialTags);
					}
					// Reset the flag after the operation
					setTimeout(() => {
						isInternalChangeRef.current = false;
					}, 100);
				}
			}, 0);
			// } else {
			//     console.log('Tags already match, no sync needed');
			// }

			lastInitialTagsRef.current = newInitialTagsJSON;
			// } else {
			//     console.log('Initial tags have not changed');
			// }
		}, [initialTags]);

		const handleSelectAll = () => {
			tagifyInstanceRef.current.removeAllTags();
			const allTags = whitelist.map((item) =>
				typeof item === "object" && item.value ? item : { value: item },
			);
			tagifyInstanceRef.current.addTags(allTags);
		};

		const handleRemoveAll = () => {
			tagifyInstanceRef.current.removeAllTags();
		};

		// Expose addTag method via ref
		useImperativeHandle(ref, () => ({
			addTag: (tag) => {
				if (tagifyInstanceRef.current) {
					// Add the tag directly - this will trigger the change event naturally
					tagifyInstanceRef.current.addTags([tag]);
				}
			},
		}));

		return (
			<div className="tagify-wrapper">
				<input
					type="text"
					ref={tagifyInputRef}
					placeholder={`Add ${type}...`}
				/>
				{whitelist.length > 0 && (
					<div className="tagify-button-group mt-2">
						<button
							type="button"
							className="tagify-button select-all-buton pl-heading-1 fw-600 text-purple-40"
							onClick={handleSelectAll}
						>
							{__("Select All", "ultimate-security")}
						</button>
						<button
							type="button"
							className="tagify-button remove-all-button pl-heading-1 fw-600 text-extra"
							onClick={handleRemoveAll}
						>
							{__("Remove All", "ultimate-security")}
						</button>
					</div>
				)}
			</div>
		);
	},
);

export default TagInput;
